import React, { useState, useEffect } from 'react';
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Typography,
  useTheme,
  useMediaQuery,
  Avatar,
} from '@mui/material';
import {
  Dashboard,
  ShoppingCart,
  AccountCircle,
  Notifications,
  Receipt,
  Payment,
  ExpandLess,
  ExpandMore,
  Person,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useRbac } from '../../context/RbacContext';
import { SimpleFooter } from '../layout/SimpleFooter';
import { Topbar } from '../layout/Topbar';
import { getRoleName } from '../../utils/roleUtils';
import { BRAND } from '../../config/brand';
import {
  NAVY,
  NAVY_DARK,
  getNavItemSx,
  getNavIconSx,
  getNavTextProps,
  getNavChildrenListSx,
} from '../../utils/portalNavStyles';

interface CustomerLayoutProps {
  children: React.ReactNode;
}

const drawerWidth = 280;

const customerMenuItems = [
  {
    id: 'dashboard',
    label: 'Dashboard',
    path: '/customer-portal',
    icon: <Dashboard />,
    permission: 'customer:dashboard',
  },
  {
    id: 'orders',
    label: 'My Orders',
    path: '/customer-portal/portal',
    icon: <ShoppingCart />,
    permission: 'customer:orders',
  },
  {
    id: 'profile',
    label: 'Profile',
    path: '/customer-portal/profile',
    icon: <AccountCircle />,
    permission: 'customer:profile',
  },
  {
    id: 'notifications',
    label: 'Notifications',
    path: '/customer-portal/notifications',
    icon: <Notifications />,
    permission: 'customer:notifications',
  },
];

export const CustomerLayout: React.FC<CustomerLayoutProps> = ({ children }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();
  const { hasRole, user } = useRbac();
  const isCustomer = hasRole('customer');
  const [expanded, setExpanded] = useState<string[]>([]);
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleExpandClick = (id: string) => {
    setExpanded(prev =>
      prev.includes(id) ? prev.filter(e => e !== id) : [...prev, id]
    );
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  const renderMenuItem = (item: any, level: number = 0) => {
    if (!isCustomer) {
      return null;
    }
    const hasChildren = item.children && item.children.length > 0;
    const isExpanded = expanded.includes(item.id);
    const isActive = item.path ? location.pathname === item.path : false;
    if (hasChildren) {
      return (
        <Box key={item.id}>
          <ListItemButton onClick={() => handleExpandClick(item.id)} sx={getNavItemSx(level, isExpanded)}>
            <ListItemIcon sx={getNavIconSx(level)}>{item.icon}</ListItemIcon>
            <ListItemText primary={item.label} primaryTypographyProps={getNavTextProps(level)} />
            {isExpanded ? <ExpandLess sx={{ color: NAVY }} /> : <ExpandMore sx={{ color: NAVY }} />}
          </ListItemButton>
          <Collapse in={isExpanded} timeout="auto">
            <List component="div" disablePadding sx={getNavChildrenListSx()}>
              {item.children && item.children.map((child: any) => renderMenuItem(child, level + 1))}
            </List>
          </Collapse>
        </Box>
      );
    }
    return (
      <ListItem key={item.id} disablePadding>
        <ListItemButton onClick={() => handleNavigation(item.path)} sx={getNavItemSx(level, isActive)} selected={isActive}>
          <ListItemIcon sx={getNavIconSx(level)}>{item.icon}</ListItemIcon>
          <ListItemText primary={item.label} primaryTypographyProps={getNavTextProps(level)} />
        </ListItemButton>
      </ListItem>
    );
  };

  const drawerContent = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Logo/Brand Section */}
      <Box
        sx={{
          p: 3,
          background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_DARK} 100%)`,
          color: 'white',
          textAlign: 'center',
        }}
      >
        <Person sx={{ fontSize: 40, mb: 1 }} />
        <Typography
          variant="h5"
          sx={{
            fontWeight: 800,
            background: 'linear-gradient(135deg, #ffffff 0%, #e9d5ff 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}
        >
          Customer Portal
        </Typography>
        <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
          Production Management System
        </Typography>
      </Box>
      {/* User Info */}
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <Avatar sx={{ mr: 2, bgcolor: NAVY }}>
            {user?.name?.[0] || 'C'}
          </Avatar>
          <Box>
            <Typography variant="body2" fontWeight="medium">
              {user?.name}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {getRoleName(user?.role as any)} • {user?.email}
            </Typography>
          </Box>
        </Box>
      </Box>
      {/* Menu Items */}
      <Box sx={{ flexGrow: 1, overflow: 'auto', py: 2 }}>
        <List>
          {customerMenuItems.map((item) => renderMenuItem(item))}
        </List>
      </Box>
      {/* Footer */}
      <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
        <Typography variant="caption" color="text.secondary" align="center" display="block">
          Customer Portal v1.0
        </Typography>
        <Typography variant="caption" color="text.secondary" align="center" display="block">
          © 2024 Monio Production
        </Typography>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', height: '100vh' }}>
      {/* Mobile Drawer */}
      {isMobile && (
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: drawerWidth,
              backgroundColor: theme.palette.background.paper,
              zIndex: theme.zIndex.drawer,
            },
          }}
        >
          {drawerContent}
        </Drawer>
      )}
      {/* Desktop Drawer */}
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: 'none', md: 'block' },
          '& .MuiDrawer-paper': { 
            boxSizing: 'border-box', 
            width: drawerWidth,
            backgroundColor: theme.palette.background.paper,
            borderRight: 1,
            borderColor: 'divider',
            zIndex: theme.zIndex.drawer,
            position: 'fixed',
            height: '100vh',
          },
        }}
        open
      >
        {drawerContent}
      </Drawer>
      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { md: `calc(100% - ${drawerWidth}px)` },
          ml: { md: `${drawerWidth}px` },
          minHeight: '100vh',
          backgroundColor: theme.palette.background.default,
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
        }}
      >
        {/* Top Bar */}
        <Topbar onDrawerToggle={() => setMobileOpen(true)} notificationsCount={4} />
        {/* Page Content */}
        <Box sx={{ 
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          mt: '64px',
        }}>
          <Box sx={{ 
            flexGrow: 1,
            p: 3,
            display: 'flex',
            flexDirection: 'column',
          }}>
            <Box sx={{ flexGrow: 1 }}>
      {children}
            </Box>
            <SimpleFooter />
          </Box>
        </Box>
      </Box>
    </Box>
  );
}; 